import {
  Modal,
  PrimaryButton,
  SecondaryButton,
  SnackbarStatus,
  Typography,
  useSnackbar,
} from '@weareopenr/component-library';
import chefPlating from 'src/assets/images/chef-plating.png';
import { useMemo, useState } from 'react';
import styles from './CreateChannelAttributeGroupFlow.module.scss';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import { useForm } from 'react-hook-form';
import { IAggregator } from '../../types/aggregator.types';
import { createChannelAttributeGroup } from '../../api/createChannelAttributeGroup/createChannelAttributeGroup';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../hooks/QueryKeys';
import { IAttributeGroup } from './types';
import { StepHeader } from './StepHeader';
import { AttributeFields } from '../AttributeFields/AttributeFields';
import { ICreateChannelAttributeGroupRequestBody } from '../../api/createChannelAttributeGroup/types';
import { Status } from '../../api/constants';
import { ItemRule } from 'src/types/menu.types';
import { updateChannelAttributeGroup } from 'src/api/updateChannelAttributeGroup/updateChannelAttributeGroup';
import { IUpdateChannelAttributeGroupRequestBody } from 'src/api/updateChannelAttributeGroup/types';
import { getAssetUrl } from 'src/helpers';
import SelectableListWithControls from '../SelectableListWithControls';

interface ICreateChannelAttributeGroupProps {
  externalSystems: IAggregator[];
  itemId: string;
  onClose: () => void;
  itemDefaultAttributes: {
    consumer_name?: string;
    description?: string;
    plu?: string;
  };
  ruleToEdit?: ItemRule | null;
}

const Content = ({
  externalSystems,
  itemId,
  onClose,
  itemDefaultAttributes,
  ruleToEdit,
}: ICreateChannelAttributeGroupProps) => {
  const imageUrl = getAssetUrl(ruleToEdit?.assets);
  const [step, setStep] = useState<number>(0);
  const [image, setImage] = useState<File | string>(imageUrl);
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting },
  } = useForm<IAttributeGroup>({
    defaultValues: {
      externalSystems: [],
      consumerName: ruleToEdit?.consumer_name,
      description: ruleToEdit?.description,
      plu: ruleToEdit?.plu,
    },
  });

  const checkboxOptions = useMemo(
    () =>
      externalSystems.map((externalSystem) => ({
        id: externalSystem.aggregatorId,
        label: externalSystem.name,
      })),
    [externalSystems],
  );

  const subTitle = step === 0 ? '' : 'Add attributes';

  const create = async (data: IAttributeGroup) => {
    const payload: ICreateChannelAttributeGroupRequestBody = {
      rules: data.externalSystems.map((externalSysId) => ({
        external_system_id: externalSysId,
        consumer_name: data.consumerName || null,
        plu: data.plu || null,
        description: data.description || null,
      })),
    };

    try {
      const res = await createChannelAttributeGroup(itemId, payload, typeof image === 'string' ? undefined : image);
      if (res.status !== Status.SUCCESS) {
        const json = await res.json();
        throw new Error(json);
      }
      await queryClient.invalidateQueries([QueryKeys.item, itemId]);
      onClose();
      showSnackbar({ status: SnackbarStatus.SUCCESS, message: 'Attribute group added' });
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'Something went wrong whilst creating an attribute group',
      });
    }
  };

  const edit = async (data: IAttributeGroup) => {
    const payload: IUpdateChannelAttributeGroupRequestBody = {
      plu: data.plu,
      consumer_name: data.consumerName,
      description: data.description,
    };

    try {
      const res = await updateChannelAttributeGroup(itemId, payload, image, ruleToEdit.external_system_id);
      if (res.status !== Status.SUCCESS) {
        throw new Error();
      }
      await queryClient.invalidateQueries([QueryKeys.item, itemId]);
      onClose();
      showSnackbar({ status: SnackbarStatus.SUCCESS, message: 'Attribute group updated' });
    } catch (error) {
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'Attributes group failed to update. Please try again.' });
    }
  };

  const submit = ruleToEdit ? edit : create;

  const extSystems = watch('externalSystems');

  return (
    <div className={styles['content-container']}>
      {!ruleToEdit && (
        <Typography size="small" className={styles['step']}>
          {`Step ${step + 1} of 2`}
        </Typography>
      )}
      <form onSubmit={handleSubmit(submit)}>
        <StepHeader
          title={`${ruleToEdit ? 'Edit' : 'Create'} Channel Attributes`}
          subTitle={ruleToEdit ? '' : subTitle}
        />
        {step === 0 && !ruleToEdit && (
          <div className={styles['modal-content']}>
            <Typography size="medium" weight="regular">
              Apply the publishing channels to the channel specific attribute group. The channel specific attributes
              within the attribute group will only be published if a publishing channel is selected.
            </Typography>
            <SelectableListWithControls
              options={checkboxOptions}
              selectedOptionsIds={extSystems}
              onOptionChange={(selectedOptions) => setValue('externalSystems', selectedOptions)}
              listDetails={{ title: 'Channels:' }}
            />
          </div>
        )}
        {(step === 1 || ruleToEdit) && (
          <AttributeFields
            isEditMode={!!ruleToEdit}
            register={register}
            image={image}
            setImage={setImage}
            placeholders={{
              consumer_name: itemDefaultAttributes.consumer_name,
              description: itemDefaultAttributes.description,
              posId: itemDefaultAttributes.plu,
            }}
          />
        )}

        <ButtonContainer
          left={
            <SecondaryButton
              buttonStyle="outline"
              type="button"
              onClick={() => {
                if (step === 0) {
                  onClose();
                } else {
                  setStep(0);
                }
              }}
              size="full-width"
            >
              {step === 0 ? 'Cancel' : 'Back'}
            </SecondaryButton>
          }
          right={
            <>
              {step < 1 && !ruleToEdit ? (
                <PrimaryButton
                  onClick={async (e) => {
                    e.preventDefault();
                    setStep(1);
                  }}
                  disabled={extSystems.length < 1}
                  colour="blueberry"
                  size="full-width"
                  type="button"
                >
                  Next
                </PrimaryButton>
              ) : (
                <PrimaryButton loading={isSubmitting} type="submit" colour="blueberry" size="full-width">
                  Confirm
                </PrimaryButton>
              )}
            </>
          }
        />
      </form>
    </div>
  );
};

export const CreateChannelAttributeGroupFlow = ({
  open,
  onClose,
  externalSystems,
  itemId,
  itemDefaultAttributes,
  ruleToEdit,
}: ICreateChannelAttributeGroupProps & {
  open: boolean;
}) => {
  return (
    <Modal open={open} closeModal={() => onClose()} image={chefPlating} imageAlt="chef plating">
      <Content
        externalSystems={externalSystems}
        itemId={itemId}
        onClose={onClose}
        itemDefaultAttributes={itemDefaultAttributes}
        ruleToEdit={ruleToEdit}
      />
    </Modal>
  );
};
